import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, UserCog, Shield, ShieldCheck, Mail, Lock, User, Calculator, Crown } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Resumo das regras de acesso de cada perfil (para se ter noção)
const ROLE_RULES = [
  {
    name: 'Administrador Principal',
    icon: Crown,
    chip: 'bg-blue-600',
    desc: 'A conta principal do grupo (a sua). Acesso total a tudo e a ÚNICA que pode criar/gerir gestores, contabilistas e administradores (esta página).',
  },
  {
    name: 'Administrador',
    icon: Shield,
    chip: 'bg-blue-500',
    desc: 'Acesso total ao sistema (RH e, no futuro, Financeiro). Não pode gerir gestores nem entrar na Administração do grupo.',
  },
  {
    name: 'Gestor',
    icon: UserCog,
    chip: 'bg-green-500',
    desc: 'Acesso total ao sistema. Não pode gerir gestores. Indicado para responsáveis que precisam de fazer tudo no dia a dia.',
  },
  {
    name: 'Contabilista',
    icon: Calculator,
    chip: 'bg-teal-500',
    desc: 'Acesso total ao sistema (ex.: o contabilista do grupo). Não pode gerir gestores.',
  },
  {
    name: 'Colaborador',
    icon: User,
    chip: 'bg-slate-500',
    desc: 'Apenas a sua área: registar ponto (com localização), pedir férias/ausências, ver documentos e editar o seu perfil (foto e dados pessoais). Sem acesso à gestão.',
  },
];

export default function AdminManagers() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'gerente'
  });
  const [errors, setErrors] = useState({});

  // Edição (clique na linha): nome, email, tipo de acesso e password opcional
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: 'gerente', new_password: '' });
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API_URL}/admins`);
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
      if (error.response?.status !== 403) {
        toast.error('Erro ao carregar gestores');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    if (!formData.password) {
      newErrors.password = 'Palavra-passe é obrigatória';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Palavra-passe deve ter pelo menos 8 caracteres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/admins`, formData);
      toast.success(`${formData.role === 'admin' ? 'Administrador' : formData.role === 'contabilista' ? 'Contabilista' : 'Gestor'} criado com sucesso!`);
      setDialogOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'gerente' });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar utilizador');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (adminId, adminName) => {
    try {
      await axios.delete(`${API_URL}/admins/${adminId}`);
      toast.success(`${adminName} eliminado com sucesso`);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar');
    }
  };

  const openEdit = (admin) => {
    setEditingAdmin(admin);
    setEditForm({ name: admin.name || '', email: admin.email || '', role: admin.role || 'gerente', new_password: '' });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) { toast.error('O nome é obrigatório'); return; }
    if (editForm.new_password && editForm.new_password.length < 8) {
      toast.error('A palavra-passe nova deve ter pelo menos 8 caracteres'); return;
    }
    setEditLoading(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        role: editForm.role,
      };
      if (editForm.new_password) payload.new_password = editForm.new_password;
      await axios.put(`${API_URL}/admins/${editingAdmin.id}`, payload);
      toast.success(`${payload.name} atualizado com sucesso`);
      setEditDialogOpen(false);
      setEditingAdmin(null);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar utilizador');
    } finally {
      setEditLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-blue-500">Administrador</Badge>;
      case 'gerente':
        return <Badge className="bg-green-500">Gestor</Badge>;
      case 'contabilista':
        return <Badge className="bg-teal-500">Contabilista</Badge>;
      default:
        return <Badge variant="outline">{role}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={ShieldCheck}
        title="Administração do grupo"
        subtitle="Definições que valem para todo o grupo Lisbonb · gerir gestores e acessos"
      >
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-manager-btn">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Gestor
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Novo Gestor/Administrador</DialogTitle>
              <DialogDescription>
                Crie um novo utilizador com permissões administrativas. O utilizador receberá uma senha temporária e terá que alterá-la no primeiro acesso.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    placeholder="Nome completo"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      setErrors({ ...errors, name: '' });
                    }}
                    className={`pl-10 ${errors.name ? 'border-red-500' : ''}`}
                    data-testid="manager-name-input"
                  />
                </div>
                {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@empresa.com"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      setErrors({ ...errors, email: '' });
                    }}
                    className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                    data-testid="manager-email-input"
                  />
                </div>
                {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha Temporária</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Mínimo 8 caracteres"
                    value={formData.password}
                    onChange={(e) => {
                      setFormData({ ...formData, password: e.target.value });
                      setErrors({ ...errors, password: '' });
                    }}
                    className={`pl-10 ${errors.password ? 'border-red-500' : ''}`}
                    data-testid="manager-password-input"
                  />
                </div>
                {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
                <p className="text-xs text-muted-foreground">
                  O utilizador terá que alterar esta senha no primeiro acesso
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Função</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="manager-role-select">
                    <SelectValue placeholder="Selecione a função" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gerente">
                      <div className="flex items-center gap-2">
                        <UserCog className="h-4 w-4" />
                        <span>Gestor</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="contabilista">
                      <div className="flex items-center gap-2">
                        <Calculator className="h-4 w-4" />
                        <span>Contabilista</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        <span>Administrador</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Gestor, Contabilista e Administrador têm <strong>acesso completo</strong> ao sistema.
                  A única coisa que não podem fazer é criar/gerir estes perfis — isso é exclusivo
                  da conta principal (a sua).
                </p>
              </div>

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={formLoading} data-testid="create-manager-btn">
                  {formLoading ? 'A criar...' : 'Criar Utilizador'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {/* Regras de acesso por perfil */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Regras de acesso por perfil
          </CardTitle>
          <CardDescription>
            O que cada tipo de utilizador pode fazer no sistema.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {ROLE_RULES.map((r) => (
            <div key={r.name} className="flex items-start gap-3 p-3 rounded-xl border bg-muted/30">
              <div className={`h-9 w-9 rounded-lg ${r.chip} text-white flex items-center justify-center shrink-0`}>
                <r.icon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-sm">{r.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{r.desc}</p>
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground pt-1">
            💡 Hoje, <strong>Gestor</strong>, <strong>Contabilista</strong> e <strong>Administrador</strong> têm
            o mesmo nível de acesso (tudo, exceto gerir gestores) — a diferença é o título/função.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5" />
            Lista de Gestores
          </CardTitle>
          <CardDescription>
            Utilizadores com permissões administrativas no sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          {admins.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <UserCog className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum gestor ou administrador adicional criado</p>
              <p className="text-sm">Clique em "Adicionar Gestor" para criar um novo</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Função</TableHead>
                  <TableHead>Data de Criação</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.id} onClick={() => openEdit(admin)}
                    className="cursor-pointer hover:bg-muted/50"
                    title="Clique para editar" data-testid={`admin-row-${admin.id}`}>
                    <TableCell className="font-medium">{admin.name}</TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>{getRoleBadge(admin.role)}</TableCell>
                    <TableCell>
                      {new Date(admin.created_at).toLocaleDateString('pt-PT')}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminar {admin.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta ação não pode ser revertida. O utilizador perderá acesso ao sistema imediatamente.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              onClick={() => handleDelete(admin.id, admin.name)}
                            >
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog de edição (clique numa linha da lista) */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent data-testid="edit-admin-dialog">
          <DialogHeader>
            <DialogTitle>Editar {editingAdmin?.name}</DialogTitle>
            <DialogDescription>
              Altere o nome, o email, o tipo de acesso ou defina uma palavra-passe nova.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nome *</Label>
              <Input id="edit-name" value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required data-testid="edit-admin-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email *</Label>
              <Input id="edit-email" type="email" value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                required data-testid="edit-admin-email" />
            </div>
            <div className="space-y-2">
              <Label>Tipo de acesso</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-admin-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gerente">Gestor</SelectItem>
                  <SelectItem value="contabilista">Contabilista</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-password">Palavra-passe nova (opcional)</Label>
              <Input id="edit-password" type="password" value={editForm.new_password}
                onChange={(e) => setEditForm({ ...editForm, new_password: e.target.value })}
                placeholder="Deixe vazio para manter a atual" data-testid="edit-admin-password" />
              <p className="text-xs text-muted-foreground">
                Se definir uma nova, o utilizador terá de a trocar no próximo login.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={editLoading} data-testid="save-admin-btn">
                {editLoading ? 'A guardar...' : 'Guardar alterações'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
